import { ChecklistStep } from './types';

/**
 * Activation checklist steps definition
 * These steps guide users to "answer a real hangar question" through:
 * 1. Adding aircraft
 * 2. Running AutoStack twice (to encourage experimentation)
 * 3. Saving a layout (commitment signal)
 * 4. Sharing with team (social proof / value demonstration)
 */
// Event names for activation triggers
export const ACTIVATION_EVENTS = {
  ADD_AIRCRAFT: 'activation:add_aircraft',
  RUN_AUTOSTACK: 'activation:run_autostack',
  SAVE_LAYOUT: 'activation:save_layout',
  SHARE_LAYOUT: 'activation:share_layout',
} as const;

// Spotlight IDs for target elements (used by SpotlightPulse component)
export const SPOTLIGHT_IDS = {
  ADD_AIRCRAFT_BUTTON: 'spotlight-add-aircraft-button', // Outer button that opens dialog
  ADD_AIRCRAFT: 'spotlight-add-aircraft', // Inner "Add" button in dialog
  AUTOSTACK: 'spotlight-autostack',
  SAVE_LAYOUT: 'spotlight-save-layout',
  PRINT: 'spotlight-print',
} as const;

export const ACTIVATION_STEPS: ChecklistStep[] = [
  {
    id: 'add_aircraft',
    titleKey: 'components.activation.steps.addAircraft.title',
    descriptionKey: 'components.activation.steps.addAircraft.description',
    completedAtField: 'step_add_aircraft_at',
    action: {
      type: 'trigger',
      event: ACTIVATION_EVENTS.ADD_AIRCRAFT,
      spotlightId: SPOTLIGHT_IDS.ADD_AIRCRAFT_BUTTON, // Highlight the button that opens dialog
    },
  },
  {
    id: 'first_autostack',
    titleKey: 'components.activation.steps.firstAutostack.title',
    descriptionKey: 'components.activation.steps.firstAutostack.description',
    completedAtField: 'step_first_autostack_at',
    action: {
      type: 'trigger',
      event: ACTIVATION_EVENTS.RUN_AUTOSTACK,
      spotlightId: SPOTLIGHT_IDS.AUTOSTACK,
    },
  },
  {
    id: 'second_autostack',
    titleKey: 'components.activation.steps.secondAutostack.title',
    descriptionKey: 'components.activation.steps.secondAutostack.description',
    completedAtField: 'step_second_autostack_at',
    action: {
      type: 'trigger',
      event: ACTIVATION_EVENTS.RUN_AUTOSTACK,
      spotlightId: SPOTLIGHT_IDS.AUTOSTACK,
    },
  },
  {
    id: 'save_favorite',
    titleKey: 'components.activation.steps.saveFavorite.title',
    descriptionKey: 'components.activation.steps.saveFavorite.description',
    completedAtField: 'step_save_favorite_at',
    action: {
      type: 'trigger',
      event: ACTIVATION_EVENTS.SAVE_LAYOUT,
      spotlightId: SPOTLIGHT_IDS.SAVE_LAYOUT,
    },
  },
  {
    id: 'share_layout',
    titleKey: 'components.activation.steps.shareLayout.title',
    descriptionKey: 'components.activation.steps.shareLayout.description',
    completedAtField: 'step_share_layout_at',
    action: {
      type: 'trigger',
      event: ACTIVATION_EVENTS.SHARE_LAYOUT,
      spotlightId: SPOTLIGHT_IDS.PRINT,
    },
  },
];

/**
 * Get the current step index (first incomplete step)
 */
export function getCurrentStepIndex(
  activation: { [K in (typeof ACTIVATION_STEPS)[number]['completedAtField']]: string | null }
): number {
  for (let i = 0; i < ACTIVATION_STEPS.length; i++) {
    const step = ACTIVATION_STEPS[i];
    if (!activation[step.completedAtField]) {
      return i;
    }
  }
  return ACTIVATION_STEPS.length; // All complete
}

/**
 * Get count of completed steps
 */
export function getCompletedCount(
  activation: { [K in (typeof ACTIVATION_STEPS)[number]['completedAtField']]: string | null }
): number {
  return ACTIVATION_STEPS.filter((step) => activation[step.completedAtField]).length;
}
